import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Fade, Typography } from '@mui/material';
import FuseDialog from '@fuse/core/FuseDialog';
import { ControlledTextField } from 'app/shared-components';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import useShowMessage from 'app/hooks/useShowMessage';
import { MESSAGE_TYPE } from 'app/constants/message';
import yup from 'app/helpers/validation';
import DIALOGS from 'app/constants/dialogs';
import { TRANSLATION_KEY } from '../constants';
import { actions, selectors, STATUS } from '../store/billingSlice';
import RequestScalePlanSuccess from './RequestScalePlanSuccess';

const FADE_DURATION = 200;
const RESIZE_DURATION = 300;

const defaultValues = {
  estimated_verification_volume: '',
  email: '',
  message: '',
};

const RequestScalePlanDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const showMessage = useShowMessage();
  const requestStatus = useSelector(selectors.selectRequestCustomPlanStatus);
  const loading = requestStatus === STATUS.PENDING;
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(true);

  const schema = useMemo(
    () =>
      yup.object().shape({
        estimated_verification_volume: yup.string().required(t('VOLUME_REQUIRED')),
        email: yup.string().required(t('EMAIL_REQUIRED')).email(t('EMAIL_INVALID')),
        message: yup.string(),
      }),
    [t]
  );

  const { control, handleSubmit, formState, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { isValid, dirtyFields } = formState;

  const onClose = useCallback(() => {
    dispatch(closeDialog());
    setTimeout(() => {
      setSubmitted(false);
      setVisible(true);
      reset(defaultValues);
      dispatch(actions.clearStatus('requestCustomPlan'));
    }, RESIZE_DURATION);
  }, [dispatch, reset]);

  const onSubmit = handleSubmit((data) => {
    dispatch(actions.requestCustomPlan(data))
      .unwrap()
      .then(() => {
        setVisible(false);
        setTimeout(() => {
          setSubmitted(true);
          setVisible(true);
        }, FADE_DURATION);
      })
      .catch((error) => {
        showMessage(error, MESSAGE_TYPE.ERROR);
      });
  });

  return (
    <FuseDialog
      fullWidth
      maxWidth={submitted ? 'sm' : 'md'}
      dialogId={DIALOGS.REQUEST_SCALE_PLAN}
      onClose={onClose}
      PaperProps={{
        sx: {
          transition: `max-width ${RESIZE_DURATION}ms ease`,
          overflow: 'hidden',
          maxWidth: '700px',
        },
      }}
    >
      <Box>
        <Fade in={visible} timeout={FADE_DURATION}>
          <Box>
            {submitted ? (
              <RequestScalePlanSuccess
                imageSrc="/assets/images/apps/billing/scale-plan-success.png"
                title={t('REQUEST_SENT_TITLE')}
                description={
                  <>
                    {t('REQUEST_SENT_DESCRIPTION')}
                    <Typography variant="body2" color="accent.500" mt="12px" component="p">
                      {t('REQUEST_SENT_EMAIL_NOTE')}
                    </Typography>
                  </>
                }
                onClose={onClose}
              />
            ) : (
              <Box display="flex" overflow="hidden" padding="32px" gap="28px">
                <Box flex={1} display="flex" flexDirection="column" gap="24px">
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="accent.900">
                      {t('REQUEST_SCALE_PLAN_TITLE')}
                    </Typography>
                    <Typography variant="body2" color="accent.500" mt="8px">
                      {t('REQUEST_SCALE_PLAN_DESCRIPTION')}
                    </Typography>
                  </Box>

                  <form onSubmit={onSubmit}>
                    <Box display="flex" flexDirection="column" gap="20px">
                      <Box>
                        <ControlledTextField
                          name="estimated_verification_volume"
                          control={control}
                          label={t('ESTIMATED_VOLUME')}
                          placeholder={t('ESTIMATED_VOLUME_PLACEHOLDER')}
                          required
                        />
                        <Typography variant="caption" color="accent.400" mt="4px">
                          {t('ESTIMATED_VOLUME_HINT')}
                        </Typography>
                      </Box>

                      <Box>
                        <ControlledTextField
                          name="email"
                          control={control}
                          label={t('CONTACT_EMAIL')}
                          placeholder={t('CONTACT_EMAIL_PLACEHOLDER')}
                          required
                        />
                        <Typography variant="caption" color="accent.400" mt="4px">
                          {t('CONTACT_EMAIL_HINT')}
                        </Typography>
                      </Box>

                      <Box>
                        <ControlledTextField
                          name="message"
                          control={control}
                          label={t('ADDITIONAL_DETAILS')}
                          placeholder={t('ADDITIONAL_DETAILS_PLACEHOLDER')}
                          multiline
                          rows={3}
                        />
                        <Typography variant="caption" color="accent.400" mt="4px">
                          {t('ADDITIONAL_DETAILS_HINT')}
                        </Typography>
                      </Box>

                      <Box display="flex" gap="16px" mt="8px">
                        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'accent.900' }}>
                          {t('CLOSE')}
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          type="submit"
                          disabled={loading || !isValid || !Object.keys(dirtyFields).length}
                          sx={{
                            borderRadius: '24px',
                            paddingX: '32px',
                            textTransform: 'none',
                            width: '100%',
                          }}
                          startIcon={loading && <CircularProgress size={16} color="inherit" />}
                        >
                          {t('REQUEST_PRICING')}
                        </Button>
                      </Box>
                    </Box>
                  </form>
                </Box>

                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/images/apps/billing/scale-plan-promo.png"
                    alt="Scale plan"
                    sx={{
                      maxWidth: '226px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Box>
    </FuseDialog>
  );
};

export default RequestScalePlanDialog;
